import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import Payment from 'components/FrictionlessCheckout/PaymentReleaseFour/Payment';
import orderUtils from 'utils/Order';
import paymentOptionsSelector from 'selectors/order/paymentOptionsSelector';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import checkoutUtils from 'utils/Checkout';
import pazeSelector from 'selectors/paze/pazeSelector';
import klarnaSelector from 'selectors/klarna/klarnaSelector';
import afterpaySelector from 'selectors/afterpay/afterpaySelector';
import venmoSelector from 'selectors/venmo/venmoSelector';
import { orderSelector } from 'selectors/order/orderSelector';
import { userSelector } from 'selectors/user/userSelector';
import {
    checkSelectedPayment, getCreditCards, commonOrderToggleActions, isAlternatePaymentMethodSelected
} from 'services/Checkout';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import orderErrorsSelector from 'selectors/order/orderErrorsSelector';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import Actions from 'Actions';
import activeCheckoutSectionSelector from 'selectors/order/activeCheckoutSectionSelector';
import OrderActions from 'actions/OrderActions';
import AfterpayActions from 'actions/AfterpayActions';
import KlarnaActions from 'actions/KlarnaActions';
import PaymentsActions from 'actions/PaymentsActions';
import UserActions from 'actions/UserActions';
import historyLocationActions from 'actions/framework/HistoryLocationActions';
import PaymentEN from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_en_US';
import PaymentFR from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_fr_CA';
import PaymentUtils from 'utils/payment';

const {
    checkGiftCardAmountEqualsOrderTotal,
    checkIsAltenateMethodDefaultPayment,
    getAvailablePaymentMethods,
    getGiftCardPaymentGroup,
    checkIfAlternateMethodSelected,
    getAlternatePaymentName,
    getInstallment,
    isAnyPaymentOptionSelected
} = PaymentUtils;

const { showInfoModal } = Actions;

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PaymentEN, PaymentFR);

const localization = createStructuredSelector({
    cardTitle: getTextFromResource(getText, 'cardTitle'),
    expires: getTextFromResource(getText, 'expires'),
    addNewCard: getTextFromResource(getText, 'addNewCard'),
    useGiftCard: getTextFromResource(getText, 'useGiftCard'),
    maxGiftCards: getTextFromResource(getText, 'maxGiftCards'),
    giftCard: getTextFromResource(getText, 'giftCard'),
    applied: getTextFromResource(getText, 'applied'),
    remove: getTextFromResource(getText, 'remove'),
    secure: getTextFromResource(getText, 'secure'),
    verifyCVV: getTextFromResource(getText, 'verifyCVV'),
    accountCredit: getTextFromResource(getText, 'accountCredit'),
    giftCardsNotCombinable: getTextFromResource(getText, 'giftCardsNotCombinable'),
    paymentMethodSelection: getTextFromResource(getText, 'paymentMethodSelection'),
    somePaymentCannotUsed: getTextFromResource(getText, 'somePaymentCannotUsed'),
    otherWaysToPay: getTextFromResource(getText, 'otherWaysToPay'),
    interestFreeMessage: getTextFromResource(getText, 'interestFreeMessage'),
    orPayInFour: getTextFromResource(getText, 'orPayInFour'),
    installmentsMessage: getTextFromResource(getText, 'installmentsMessage', ['{0}']),
    payWith: getTextFromResource(getText, 'payWith'),
    availableWith: getTextFromResource(getText, 'availableWith'),
    edit: getTextFromResource(getText, 'edit'),
    afterpayHeader: getTextFromResource(getText, 'afterpayHeader'),
    afterpaySubtitle: getTextFromResource(getText, 'afterpaySubtitle', ['{0}']),
    afterpaySaveAndContinue: getTextFromResource(getText, 'afterpaySaveAndContinue'),
    afterpayCancel: getTextFromResource(getText, 'afterpayCancel'),
    afterpayLoading: getTextFromResource(getText, 'afterpayLoading'),
    afterpayApiErrorFallback: getTextFromResource(getText, 'afterpayApiErrorFallback'),
    afterpayGiftCardWarning: getTextFromResource(getText, 'afterpayGiftCardWarning'),
    afterpayLegalNotice: getTextFromResource(getText, 'afterpayLegalNotice'),
    klarnaHeader: getTextFromResource(getText, 'klarnaHeader'),
    klarnaSubtitle: getTextFromResource(getText, 'klarnaSubtitle', ['{0}']),
    klarnaSaveAndContinue: getTextFromResource(getText, 'klarnaSaveAndContinue'),
    klarnaCancel: getTextFromResource(getText, 'klarnaCancel'),
    klarnaLoading: getTextFromResource(getText, 'klarnaLoading'),
    klarnaApiErrorFallback: getTextFromResource(getText, 'klarnaApiErrorFallback'),
    klarnaGiftCardWarning: getTextFromResource(getText, 'klarnaGiftCardWarning'),
    klarnaFeatureBuyerProtection: getTextFromResource(getText, 'klarnaFeatureBuyerProtection'),
    klarnaTermsText: getTextFromResource(getText, 'klarnaTermsText'),
    klarnaLegalNotice: getTextFromResource(getText, 'klarnaLegalNotice'),
    klarnaBillingAddressCheckbox: getTextFromResource(getText, 'klarnaBillingAddressCheckbox')
});

const fields = createSelector(
    localization,
    paymentOptionsSelector,
    orderDetailsSelector,
    pazeSelector,
    klarnaSelector,
    afterpaySelector,
    venmoSelector,
    orderErrorsSelector,
    activeCheckoutSectionSelector,
    orderSelector,
    userSelector,
    (_ownState, ownProps) => ownProps.isNewUserFlow,
    (_ownState, ownProps) => ownProps.zeroDollarHigherRedeemPointsCVV,
    (
        locale,
        paymentOptions,
        orderDetails,
        paze,
        klarna,
        afterpay,
        venmo,
        sectionErrors,
        activeSection,
        order,
        user,
        isNewUserFlow,
        zeroDollarHigherRedeemPointsCVV
    ) => {
        const creditCardPaymentGroup = orderUtils.getCreditCardPaymentGroup(orderDetails);
        // Use pending credit card if available, otherwise use server data
        const displayCreditCard = order.pendingCreditCard || creditCardPaymentGroup;
        const storeCredits = orderUtils.getStoreCredits(orderDetails);
        const isPaymentComplete = checkoutUtils.isPaymentInOrderComplete(false);
        const giftCardPaymentGroups = getGiftCardPaymentGroup(orderDetails.paymentGroups);
        const availablePaymentMethods = getAvailablePaymentMethods(orderDetails, isNewUserFlow, klarna.error);
        const payPalPaymentGroup = orderUtils.getPayPalPaymentGroup(orderDetails) || { isComplete: false };
        const venmoUsername = orderUtils.getPaymentGroup(orderDetails, 'VenmoPaymentGroup')?.username;
        const isPayPalEnabled = orderUtils.isPayPalEnabled(orderDetails);
        const showErrorIcon = checkoutUtils.renderFrictionlessPaymentErrorView() || zeroDollarHigherRedeemPointsCVV;
        const alternatePaymentName = getAlternatePaymentName(afterpay, klarna, venmo, paze, isNewUserFlow);
        const physicalGiftCardShippingGroup = orderUtils.getPhysicalGiftCardShippingGroup(orderDetails);
        const hardGoodShippingGroup = orderUtils.getHardGoodShippingGroup(orderDetails);
        const isPhysicalGiftCardOnly = physicalGiftCardShippingGroup && !hardGoodShippingGroup;
        const isZeroCheckout = orderUtils.isZeroCheckout();
        const shouldRenderGiftCardSection = !isPhysicalGiftCardOnly && !isZeroCheckout;
        const paymentSectionError = sectionErrors?.[SECTION_NAMES.PAYMENT];
        const sectionLevelError =
            (paymentSectionError?.length || (typeof paymentSectionError === 'boolean' && paymentSectionError)) && paymentSectionError;

        const tmpCardMessage = orderUtils.getTempSephoraCardMessage();
        const gcAmountNotEnoughMessage = checkoutUtils.getGiftCardAmountNotEnoughMessage(orderDetails?.paymentGroups?.paymentMessages);
        const isPhysicalGiftCardAndHardGoodShippingGroup = !!physicalGiftCardShippingGroup && !!hardGoodShippingGroup;
        const giftCardAmountEqualsOrderTotal = checkGiftCardAmountEqualsOrderTotal(orderDetails?.priceInfo);
        const hasNoPaymentOptionSelected = !isAnyPaymentOptionSelected(orderDetails.paymentGroups, order.pendingCreditCard);
        const alternateMethodSelected = checkIfAlternateMethodSelected(afterpay, klarna, paze, venmo) && !order.pendingCreditCard;

        return {
            localization: locale,
            orderDetails,
            paymentOptions,
            creditCardPaymentGroup: displayCreditCard,
            isPaymentComplete,
            storeCredits,
            giftCardPaymentGroups,
            alternateMethodSelected,
            checkSelectedPayment: checkSelectedPayment(paymentOptions, availablePaymentMethods),
            availablePaymentMethods,
            isPaypalSelected: isPayPalEnabled && payPalPaymentGroup.isComplete,
            payPalPaymentGroup,
            venmoUsername,
            alternatePaymentName,
            installmentValue: getInstallment(orderDetails),
            isCombinableGiftCards: alternatePaymentName.length === 0 || alternatePaymentName === PAYMENT_TYPES.PAY_VENMO,
            isAlternateMethodDefaultPayment: checkIsAltenateMethodDefaultPayment(alternatePaymentName, paymentOptions?.defaultPayment),
            showErrorIcon,
            shouldRenderGiftCardSection,
            sectionLevelError,
            tmpCardMessage,
            activeSection,
            getCreditCards,
            orderId: orderDetails?.header?.orderId,
            gcAmountNotEnoughMessage,
            isPhysicalGiftCardOnly,
            isPhysicalGiftCardAndHardGoodShippingGroup,
            giftCardAmountEqualsOrderTotal,
            hasNoPaymentOptionSelected,
            isAlternatePaymentMethodSelected,
            klarna,
            afterpay,
            selectedAsDefaultPaymentName: user?.selectedAsDefaultPaymentName
        };
    }
);

const withPaymentProps = wrapHOC(
    connect(fields, {
        showInfoModal,
        setActiveSection: OrderActions.setCheckoutActiveSection,
        setCheckoutSectionErrors: OrderActions.setCheckoutSectionErrors,
        clearCheckoutSectionErrors: OrderActions.clearCheckoutSectionErrors,
        commonOrderToggleActions,
        dispatchOrderUpdate: OrderActions.updateOrder,
        goToURL: historyLocationActions.goTo,
        loadExpandedViewWidget: AfterpayActions.loadExpandedViewWidget,
        savePendingCreditCard: OrderActions.savePendingCreditCard,
        klarnaSetReady: KlarnaActions.setReady,
        afterpaySetReady: AfterpayActions.setReady,
        removeDefaultPaymentFromProfile: PaymentsActions.removeDefaultPaymentFromProfile,
        toggleSelectAsDefaultPayment: UserActions.toggleSelectAsDefaultPayment,
        clearPendingCreditCard: OrderActions.clearPendingCreditCard
    })
);

export default withPaymentProps(Payment);
